import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LoginForm from './components/LoginForm';
import TrustSignals from './components/TrustSignals';
import Logo from '../../components/ui/Logo';
import Footer from '../../components/ui/Footer';
import { useAuth } from '../../contexts/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, loading, isInitialized } = useAuth();

  useEffect(() => {
    // Only redirect once auth is fully initialized and user is confirmed
    if (isInitialized && !loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, isInitialized, navigate]);

  // Show spinner while auth is initializing (first load only)
  if (loading && !isInitialized) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render login form if user is already logged in (redirect in progress)
  if (isInitialized && user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Sign In - LeadGen Pro</title>
        <meta name="description" content="Sign in to your LeadGen Pro account to access powerful AI-driven lead generation and sales tools." />
      </Helmet>

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo size="small" showText={true} linkTo="/" variant="header" />
            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">Don't have an account?</span>
              <Link to="/register">
                <button className="px-4 py-2 text-sm font-medium border border-border rounded-md text-foreground hover:bg-muted transition-colors">
                  Sign Up Free
                </button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Login Form - Main Column */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-xl p-8 shadow-elevation-1">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="mb-6">
                  <Logo size="large" showText={true} linkTo="/" variant="default" className="justify-center" />
                </div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Welcome Back</h1>
                <p className="text-muted-foreground">
                  Sign in to your account to continue generating high-quality leads
                </p>
              </div>

              <LoginForm />
            </div>
          </div>

          {/* Sidebar - Trust Signals */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              <TrustSignals />
              <div className="text-center text-sm text-muted-foreground">
                <p>Need help? Contact us at</p>
                <p className="font-medium text-foreground">support@leadgenproai.us</p>
                <p>or call (844) 978-1051</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LoginPage;